import { paths } from "@/App";
import EmptyState from "@/lib/components/EmptyState";
import {
  useAcceptInviteMutation,
  useInviteStatusQuery,
} from "@/lib/queries/invite-queries";
import { queryClient } from "@/lib/queries/query-client";
import { userQueryKeys, useSelfQuery } from "@/lib/queries/user-queries";
import { INVITE_ID_STORAGE_KEY } from "@/lib/session-storage";
import { useUser } from "@clerk/react";
import { IconUsers } from "@tabler/icons-react";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";

function Invite() {
  const navigate = useNavigate();
  const params = useParams<{ inviteExternalId: string }>();
  const { isSignedIn, isLoaded } = useUser();
  const { data: user, isSuccess, isPending: isUserPending } = useSelfQuery();
  const { data: inviteStatus, isPending: isInviteStatusPending } =
    useInviteStatusQuery(params.inviteExternalId!, {
      enabled: isLoaded && isSignedIn && isSuccess,
    });

  useEffect(() => {
    sessionStorage.removeItem(INVITE_ID_STORAGE_KEY);
  }, []);

  const { mutate: acceptInvite, isPending: isAcceptInvitePending } =
    useAcceptInviteMutation({
      inviteExternalId: params.inviteExternalId!,
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({
          queryKey: userQueryKeys.all(),
          type: "all",
        });
        navigate(paths.home(data.externalId));
      },
      onError: (error) => {
        console.error("Failed to accept invite:", error);
      },
    });

  if (!isLoaded) {
    return null;
  }

  if (isSignedIn && (isUserPending || isInviteStatusPending)) {
    return null;
  }

  const setSessionInviteId = () => {
    if (params.inviteExternalId) {
      sessionStorage.setItem(INVITE_ID_STORAGE_KEY, params.inviteExternalId);
    }
  };

  // If the user is not authenticated, show an action to sign in or register.
  if (!user) {
    return (
      <EmptyState
        icon={<IconUsers size={24} />}
        header="You need to have an account to accept an invite."
        subHeader="Please register or sign in to continue."
        primaryActionLabel="Register"
        primaryActionOnClick={() => {
          setSessionInviteId();
          navigate(paths.signUp());
        }}
        secondaryActionLabel="Sign in"
        secondaryActionOnClick={() => {
          setSessionInviteId();
          navigate(paths.signIn());
        }}
      />
    );
  }

  // If the invite does not exist show an action to go home.
  if (!inviteStatus) {
    return (
      <EmptyState
        icon={<IconUsers size={24} />}
        header="Invite not found"
        subHeader="The invite could not be found. It may have been deleted or expired."
        primaryActionLabel="Go to home page"
        primaryActionOnClick={() => navigate(paths.root())}
      />
    );
  }

  const existingMembership = user.teamMemberships.some(
    (m) => m.teamExternalId === inviteStatus?.externalId,
  );

  // If the user is already a member of the team, show an action to go to the team's videos.
  if (existingMembership) {
    return (
      <EmptyState
        icon={<IconUsers size={24} />}
        header="You are already a member"
        subHeader={`You are already a member of ${inviteStatus.name}. You can start creating videos with your team.`}
        primaryActionLabel="Go to team"
        primaryActionOnClick={() => {
          if (inviteStatus.externalId) {
            navigate(paths.home(inviteStatus.externalId));
          } else {
            navigate(paths.root());
          }
        }}
      />
    );
  }

  // If the invite exists but the email does not match, show a messsage indicating that the invite is not valid for the current user.
  if (inviteStatus.isEmailMismatch) {
    return (
      <EmptyState
        icon={<IconUsers size={24} />}
        header="Invite email is not matching."
        subHeader={`The invite email is not valid for your account.`}
        primaryActionLabel="Go to home page"
        primaryActionOnClick={() => navigate(paths.root())}
      />
    );
  }

  return (
    <div className="flex h-full items-center justify-center">
      <EmptyState
        icon={<IconUsers size={24} />}
        header={
          <span className="block first-letter:capitalize">
            {`You have been invited to join ${inviteStatus.name} 🎉`.trim()}
            <br />
          </span>
        }
        subHeader="Click the button below to accept the invite and start creating videos with your team."
        primaryActionLabel="Accept invite"
        primaryActionOnClick={() => {
          if (isAcceptInvitePending) {
            return;
          }

          acceptInvite();
        }}
        secondaryActionLabel="Decline"
        secondaryActionOnClick={() => navigate(paths.root())}
      />
    </div>
  );
}

export default Invite;
